import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, IconButton, Chip, Tooltip, TextField,
  Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, CircularProgress, Tab, Tabs, Alert, ButtonGroup, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import "dayjs/locale/es";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import AddIcon from "@mui/icons-material/Add";
import swal from "sweetalert";
import api from "../components/utils/api";

dayjs.locale("es");

const TIPO_COLORS = {
  Mantenimiento: { bg: "#1565C0", text: "#fff" },
  Limpieza:      { bg: "#2E7D32", text: "#fff" },
  Otros:         { bg: "#E65100", text: "#fff" },
};

function TipoChip({ tipo }) {
  const c = TIPO_COLORS[tipo] || { bg: "#9E9E9E", text: "#fff" };
  return (
    <Chip label={tipo || "—"} size="small"
      sx={{ bgcolor: c.bg, color: c.text, fontWeight: 600, fontSize: "0.7rem" }} />
  );
}

function KpiCard({ label, value, color = "#ED9519" }) {
  return (
    <Paper elevation={2} sx={{
      p: 2, borderRadius: 2, borderLeft: `4px solid ${color}`,
      display: "flex", flexDirection: "column", minWidth: 130,
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} color={color} mt={0.5}>{value}</Typography>
    </Paper>
  );
}

function EstadoChip({ estado, hs }) {
  if (estado === "completo") return <Chip label="✓ Completo" size="small" color="success" sx={{ fontWeight: 600 }} />;
  if (estado === "parcial")  return <Chip label="⚠ Parcial"  size="small" sx={{ bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 600 }} />;
  return <Chip label="✗ Sin horas" size="small" color="error" sx={{ fontWeight: 600 }} />;
}

// Celda del heatmap coloreada por horas
function HeatCell({ hs, jornada }) {
  const bg = hs >= jornada ? "#81C784" : hs > 0 ? "#FFB74D" : "#E0E0E0";
  const color = hs >= jornada ? "#1b5e20" : hs > 0 ? "#4e2a00" : "#555";
  return (
    <TableCell align="center" sx={{ bgcolor: bg, color, fontWeight: 700, fontSize: "0.78rem", p: "6px 4px", border: "1px solid #fff" }}>
      {hs > 0 ? `${hs}` : "—"}
    </TableCell>
  );
}

export default function ReporteHoras() {
  const userToken = localStorage.getItem("user-token");
  const headers   = { "iron-token": userToken, "Content-Type": "application/json" };
  const esAdmin   = (localStorage.getItem("roles") || "").toLowerCase().includes("admin");

  const [tab, setTab] = useState(0);

  // ── Tab 1: Detalle ────────────────────────────────────────────────────────
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [desde, setDesde]           = useState(null);
  const [hasta, setHasta]           = useState(null);

  const [editOpen, setEditOpen]             = useState(false);
  const [editCabecera, setEditCabecera]     = useState({ serie: "", ot: "", fecha: null, usuario: "" });
  const [editLineas, setEditLineas]         = useState([]);
  const [editEliminadas, setEditEliminadas] = useState([]); // ids of existing lines to DELETE
  const [editSaving, setEditSaving]         = useState(false);

  const totalHoras = rows.reduce((acc, r) => acc + Number(r.totalHoras || 0), 0).toFixed(1);
  const tecnicos   = new Set(rows.map(r => r.usuario)).size;

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)     params.append("search", search);
      if (tipoFiltro) params.append("tipoTarea", tipoFiltro);
      if (desde)      params.append("desde", desde.toISOString());
      if (hasta)      params.append("hasta", hasta.endOf("day").toISOString());
      const res = await fetch(`${api.ordenesHoras}?${params}`, { headers });
      if (!res.ok) throw new Error("Error al cargar datos");
      setRows(await res.json());
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tipoFiltro, desde, hasta]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const exportarExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (search)     params.append("search", search);
      if (tipoFiltro) params.append("tipoTarea", tipoFiltro);
      if (desde)      params.append("desde", desde.toISOString());
      if (hasta)      params.append("hasta", hasta.endOf("day").toISOString());
      const res = await fetch(`${api.ordenesHoras}export?${params}`, { headers });
      if (!res.ok) throw new Error("Error al generar Excel");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `reporte-horas-${dayjs().format("YYYY-MM-DD")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { swal("Error", err.message, "error"); }
  };

  const eliminar = async (id) => {
    const ok = await swal({ title: "¿Eliminar este registro?", icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    try {
      await fetch(`${api.ordenesHoras}${id}`, { method: "DELETE", headers });
      setRows(prev => prev.filter(r => r.id !== id));
      swal("Eliminado", "", "success");
    } catch (err) { swal("Error", err.message, "error"); }
  };

  // ── Edit helpers ─────────────────────────────────────────────────────────
  const getConflictoEdit = (lineas, idx) => {
    const a = lineas[idx];
    if (!a?.horaInicio || !a?.horaFin) return null;
    const aS = new Date(a.horaInicio), aE = new Date(a.horaFin);
    for (let j = 0; j < lineas.length; j++) {
      if (j === idx) continue;
      const b = lineas[j];
      if (!b?.horaInicio || !b?.horaFin) continue;
      const bS = new Date(b.horaInicio), bE = new Date(b.horaFin);
      if (aS < bE && aE > bS) return j + 1;
    }
    return null;
  };

  const combinarFechaHora = (fechaISO, horaVal) => {
    const f = dayjs(fechaISO);
    return f.hour(horaVal.hour()).minute(horaVal.minute()).second(0).toISOString();
  };

  const actualizarLineaEdit = (idx, field, value) =>
    setEditLineas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

  const eliminarLineaEdit = (idx, id) => {
    if (id) setEditEliminadas(prev => [...prev, id]);
    setEditLineas(prev => prev.filter((_, i) => i !== idx));
  };

  const agregarLineaEdit = () =>
    setEditLineas(prev => [...prev, {
      id: null, tipoTarea: "", horaInicio: null, horaFin: null,
      descripcion: "", tiempoViajeIda: 0, tiempoViajeVuelta: 0,
    }]);

  // ── Open edit ────────────────────────────────────────────────────────────
  const abrirEdicion = (row) => {
    const fechaBase = dayjs(row.fecha).format("YYYY-MM-DD");
    const grupo = rows.filter(r =>
      r.ordenTrabajo === row.ordenTrabajo &&
      r.usuario === row.usuario &&
      dayjs(r.fecha).format("YYYY-MM-DD") === fechaBase
    );
    setEditCabecera({ serie: row.serie || "", ot: row.ordenTrabajo || "", fecha: row.fecha, usuario: row.usuario || "" });
    setEditLineas(grupo.map(r => ({
      id: r.id,
      tipoTarea: r.tipoTarea || "",
      horaInicio: r.horaInicio || null,
      horaFin: r.horaFin || null,
      descripcion: r.descripcion || "",
      tiempoViajeIda: r.tiempoViajeIda ?? 0,
      tiempoViajeVuelta: r.tiempoViajeVuelta ?? 0,
    })));
    setEditEliminadas([]);
    setEditOpen(true);
  };

  const guardarEdicion = async () => {
    if (!editCabecera.ot?.trim() && !editCabecera.serie?.trim()) {
      swal("Campos requeridos", "Completá al menos OT o Serie antes de guardar.", "warning");
      return;
    }
    setEditSaving(true);
    try {
      const ops = [];
      for (const linea of editLineas) {
        const total = linea.horaInicio && linea.horaFin
          ? Math.abs(new Date(linea.horaFin) - new Date(linea.horaInicio)) / 36e5 : 0;
        const body = {
          ordenTrabajo: editCabecera.ot,
          serie: editCabecera.serie,
          tipoTarea: linea.tipoTarea,
          horaInicio: linea.horaInicio,
          horaFin: linea.horaFin,
          descripcion: linea.descripcion,
          totalHoras: total,
          tiempoViajeIda: parseFloat(linea.tiempoViajeIda) || 0,
          tiempoViajeVuelta: parseFloat(linea.tiempoViajeVuelta) || 0,
          usuario: editCabecera.usuario,
          fecha: editCabecera.fecha,
        };
        if (linea.id) {
          ops.push(fetch(`${api.ordenesHoras}${linea.id}`, { method: "PATCH", headers, body: JSON.stringify(body) }));
        } else {
          ops.push(fetch(api.ordenesHoras, { method: "POST", headers, body: JSON.stringify(body) }));
        }
      }
      for (const id of editEliminadas) {
        ops.push(fetch(`${api.ordenesHoras}${id}`, { method: "DELETE", headers }));
      }
      const results = await Promise.all(ops);
      if (results.some(r => !r.ok)) throw new Error("Error al guardar algunos cambios");
      setEditOpen(false);
      cargarDatos();
      swal("Guardado", "", "success");
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setEditSaving(false); }
  };

  // ── Tab 2: Alerta diaria ──────────────────────────────────────────────────
  const [alertaFecha, setAlertaFecha]     = useState(dayjs());
  const [alertaData, setAlertaData]       = useState(null);
  const [alertaLoading, setAlertaLoading] = useState(false);
  const [envioLoading, setEnvioLoading]   = useState(false);
  const [filtroEstado, setFiltroEstado]   = useState("todos");

  const cargarAlerta = useCallback(async () => {
    setAlertaLoading(true); setAlertaData(null);
    try {
      const fecha = alertaFecha ? alertaFecha.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
      const res = await fetch(`${api.ordenesHoras}alerta?fecha=${fecha}`, { headers });
      if (!res.ok) throw new Error("Error al consultar alerta");
      setAlertaData(await res.json());
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setAlertaLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertaFecha]);

  useEffect(() => { if (tab === 1) cargarAlerta(); }, [tab, cargarAlerta]);

  const enviarRecordatorio = async (modoEnvio) => {
    if (!alertaData?.faltantes?.length) return;
    setEnvioLoading(true);
    try {
      const res = await fetch(`${api.ordenesHoras}recordatorio`, {
        method: "POST", headers,
        body: JSON.stringify({ fecha: alertaFecha?.format("YYYY-MM-DD"), faltantes: alertaData.faltantes, modoEnvio }),
      });
      if (!res.ok) throw new Error("Error al enviar");
      const { enviados } = await res.json();
      swal("Enviado", `Se enviaron ${enviados.length} recordatorio(s)`, "success");
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setEnvioLoading(false); }
  };

  const usuariosFiltrados = alertaData?.usuarios?.filter(u =>
    filtroEstado === "todos" ? true : u.estado === filtroEstado
  ) || [];

  // ── Tab 3: Reporte por rango ──────────────────────────────────────────────
  const semanaAtras = dayjs().subtract(6, "day");
  const [rangoDesde, setRangoDesde]   = useState(semanaAtras);
  const [rangoHasta, setRangoHasta]   = useState(dayjs());
  const [rangoData, setRangoData]     = useState(null);
  const [rangoLoading, setRangoLoading] = useState(false);

  const cargarRango = useCallback(async () => {
    if (!rangoDesde || !rangoHasta) return;
    setRangoLoading(true); setRangoData(null);
    try {
      const params = new URLSearchParams({
        desde: rangoDesde.format("YYYY-MM-DD"),
        hasta: rangoHasta.format("YYYY-MM-DD"),
      });
      const res = await fetch(`${api.ordenesHoras}reporte-rango?${params}`, { headers });
      if (!res.ok) throw new Error("Error al cargar reporte");
      setRangoData(await res.json());
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setRangoLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangoDesde, rangoHasta]);

  useEffect(() => { if (tab === 2) cargarRango(); }, [tab, cargarRango]);

  const exportarRango = async () => {
    try {
      const params = new URLSearchParams({
        desde: rangoDesde.format("YYYY-MM-DD"),
        hasta: rangoHasta.format("YYYY-MM-DD"),
      });
      const res = await fetch(`${api.ordenesHoras}export-rango?${params}`, { headers });
      if (!res.ok) throw new Error("Error al exportar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `heatmap-${rangoDesde.format("YYYY-MM-DD")}-${rangoHasta.format("YYYY-MM-DD")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { swal("Error", err.message, "error"); }
  };

  // Formatear día para el heatmap (DD/MM)
  const fmtDia = (iso) => dayjs(iso).format("DD/MM");
  const fmtDiaSemana = (iso) => dayjs(iso).format("ddd DD/MM");

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Reporte de horas</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
        mb: 3,
        "& .MuiTab-root": { fontWeight: 600 },
        "& .MuiTabs-indicator": { backgroundColor: "#ED9519" },
        "& .Mui-selected": { color: "#ED9519 !important" },
      }}>
        <Tab label="Detalle de horas" />
        <Tab label="Alerta diaria" />
        <Tab label="Reporte por rango" />
      </Tabs>

      {/* ══════════════════════ TAB 1: DETALLE ══════════════════════════════ */}
      {tab === 0 && (
        <>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <KpiCard label="Registros"   value={rows.length} />
            <KpiCard label="Total horas" value={`${totalHoras} hs`} />
            <KpiCard label="Técnicos"    value={tecnicos} color="#1565C0" />
          </Box>

          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField label="Buscar OT / técnico" size="small" fullWidth value={search} onChange={e => setSearch(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField label="Tipo de tarea" select size="small" fullWidth value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Mantenimiento">Mantenimiento</MenuItem>
                    <MenuItem value="Limpieza">Limpieza</MenuItem>
                    <MenuItem value="Otros">Otros</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker label="Desde" value={desde} onChange={setDesde} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker label="Hasta" value={hasta} onChange={setHasta} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                </Grid>
                <Grid item xs="auto">
                  <Button variant="outlined" startIcon={<ClearIcon />} onClick={() => { setSearch(""); setTipoFiltro(""); setDesde(null); setHasta(null); }} size="small">Limpiar</Button>
                </Grid>
                <Grid item xs="auto" sx={{ ml: "auto" }}>
                  <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportarExcel} size="small"
                    sx={{ bgcolor: "#ED9519", "&:hover": { bgcolor: "#c97d0e" }, fontWeight: 700 }}>
                    Exportar Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress sx={{ color: "#ED9519" }} /></Box>
          ) : (
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700 } }}>
                    <TableCell>Fecha</TableCell><TableCell>Técnico</TableCell><TableCell>OT</TableCell>
                    <TableCell>Tipo</TableCell><TableCell>Inicio</TableCell><TableCell>Fin</TableCell>
                    <TableCell>Total</TableCell><TableCell>Descripción</TableCell><TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin registros</TableCell></TableRow>
                  ) : rows.map((r, i) => (
                    <TableRow key={r.id} sx={{ bgcolor: i % 2 === 0 ? "transparent" : "action.hover", "&:hover": { bgcolor: "action.selected" } }}>
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.82rem" }}>{r.fecha ? dayjs(r.fecha).format("DD/MM/YYYY") : "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{r.usuario}</TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{r.ordenTrabajo}</TableCell>
                      <TableCell><TipoChip tipo={r.tipoTarea} /></TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{r.horaInicio ? dayjs(r.horaInicio).format("HH:mm") : "—"}</TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>{r.horaFin ? dayjs(r.horaFin).format("HH:mm") : "—"}</TableCell>
                      <TableCell>
                        <Chip label={`${Number(r.totalHoras || 0).toFixed(1)} hs`} size="small" sx={{ bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={r.descripcion || ""} arrow>
                          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200, fontSize: "0.8rem" }}>
                            {r.descripcion || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {esAdmin && <>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEdicion(r)} color="primary"><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton size="small" onClick={() => eliminar(r.id)} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ══════════════════════ TAB 2: ALERTA ═══════════════════════════════ */}
      {tab === 1 && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
              <DatePicker label="Fecha a verificar" value={alertaFecha} onChange={v => setAlertaFecha(v)} slotProps={{ textField: { size: "small" } }} />
              <Button variant="contained" onClick={cargarAlerta} disabled={alertaLoading}
                sx={{ bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 700, "&:hover": { bgcolor: "#c97d0e" } }}>
                {alertaLoading ? <CircularProgress size={20} color="inherit" /> : "Verificar"}
              </Button>
            </Box>

            {alertaData && (
              <>
                {alertaData.esFinDeSemana ? (
                  <Alert severity="info">El día seleccionado es sábado o domingo — no se requiere carga de horas.</Alert>
                ) : (
                  <>
                    {alertaData.faltantes?.length === 0 ? (
                      <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
                        <strong>¡Todo en orden!</strong> Todos completaron sus {alertaData.horasJornada}hs el {dayjs(alertaData.fecha).format("DD/MM/YYYY")}.
                      </Alert>
                    ) : (
                      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                        <strong>{alertaData.faltantes.length}</strong> de <strong>{alertaData.totalActivos}</strong> usuarios
                        no completaron las {alertaData.horasJornada}hs del {dayjs(alertaData.fecha).format("DD/MM/YYYY")}.
                      </Alert>
                    )}

                    {/* Filtro por estado */}
                    <Box sx={{ mb: 2 }}>
                      <ToggleButtonGroup value={filtroEstado} exclusive onChange={(_, v) => v && setFiltroEstado(v)} size="small">
                        <ToggleButton value="todos">Todos ({alertaData.usuarios?.length})</ToggleButton>
                        <ToggleButton value="completo" sx={{ "&.Mui-selected": { bgcolor: "#2E7D32", color: "#fff" } }}>
                          ✓ Completo ({alertaData.usuarios?.filter(u => u.estado === "completo").length})
                        </ToggleButton>
                        <ToggleButton value="parcial" sx={{ "&.Mui-selected": { bgcolor: "#ED9519", color: "#1a0e00" } }}>
                          ⚠ Parcial ({alertaData.usuarios?.filter(u => u.estado === "parcial").length})
                        </ToggleButton>
                        <ToggleButton value="sin_horas" sx={{ "&.Mui-selected": { bgcolor: "#d32f2f", color: "#fff" } }}>
                          ✗ Sin horas ({alertaData.usuarios?.filter(u => u.estado === "sin_horas").length})
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ "& th": { bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700 } }}>
                            <TableCell>Usuario</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell align="center">Horas cargadas</TableCell>
                            <TableCell align="center">Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {usuariosFiltrados.map((u, i) => (
                            <TableRow key={u.id} sx={{ bgcolor: i % 2 === 0 ? "transparent" : "action.hover" }}>
                              <TableCell sx={{ fontWeight: 600 }}>{u.nombre}</TableCell>
                              <TableCell sx={{ color: "text.secondary", fontSize: "0.82rem" }}>{u.email || "—"}</TableCell>
                              <TableCell align="center">
                                <Chip label={`${u.horasCargadas} hs`} size="small" sx={{
                                  fontWeight: 700,
                                  bgcolor: u.estado === "completo" ? "#2E7D32" : u.estado === "parcial" ? "#ED9519" : "#9E9E9E",
                                  color: u.estado === "parcial" ? "#1a0e00" : "#fff",
                                }} />
                              </TableCell>
                              <TableCell align="center"><EstadoChip estado={u.estado} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {alertaData.faltantes?.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" mb={1} fontWeight={600}>
                          Enviar recordatorio a usuarios con horas incompletas:
                        </Typography>
                        <ButtonGroup variant="contained" disabled={envioLoading}>
                          <Button startIcon={<EmailIcon />} onClick={() => enviarRecordatorio("admin")}
                            sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0d47a1" } }}>Al administrador</Button>
                          <Button startIcon={<EmailIcon />} onClick={() => enviarRecordatorio("tecnicos")}
                            sx={{ bgcolor: "#2E7D32", "&:hover": { bgcolor: "#1b5e20" } }}>A cada técnico</Button>
                          <Button startIcon={<EmailIcon />} onClick={() => enviarRecordatorio("ambos")}
                            sx={{ bgcolor: "#ED9519", color: "#1a0e00", "&:hover": { bgcolor: "#c97d0e" } }}>A todos</Button>
                        </ButtonGroup>
                        {envioLoading && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CircularProgress size={16} /><Typography variant="caption">Enviando emails…</Typography>
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          * Solo se envía a técnicos que tengan email registrado en el sistema.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Paper>
        </LocalizationProvider>
      )}

      {/* ══════════════════════ TAB 3: REPORTE POR RANGO ════════════════════ */}
      {tab === 2 && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          {/* Selector de rango */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm="auto">
                <DatePicker label="Desde" value={rangoDesde} onChange={setRangoDesde} slotProps={{ textField: { size: "small" } }} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <DatePicker label="Hasta" value={rangoHasta} onChange={setRangoHasta} slotProps={{ textField: { size: "small" } }} />
              </Grid>
              <Grid item xs="auto">
                <Button variant="contained" onClick={cargarRango} disabled={rangoLoading}
                  sx={{ bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 700, "&:hover": { bgcolor: "#c97d0e" } }}>
                  {rangoLoading ? <CircularProgress size={20} color="inherit" /> : "Generar reporte"}
                </Button>
              </Grid>
              {rangoData && (
                <Grid item xs="auto" sx={{ ml: "auto" }}>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportarRango}
                    sx={{ borderColor: "#ED9519", color: "#ED9519", fontWeight: 700 }}>
                    Exportar Excel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>

          {rangoLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress sx={{ color: "#ED9519" }} /></Box>
          )}

          {rangoData && !rangoLoading && (
            <>
              {/* Gráfico de barras apiladas */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={2}>
                  Cumplimiento por día
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rangoData.statsPorDia} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="dia" tickFormatter={fmtDia} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ReTooltip
                      formatter={(v, name) => [v, name === "completos" ? "Completo (≥8hs)" : name === "parciales" ? "Parcial (<8hs)" : "Sin horas"]}
                      labelFormatter={fmtDiaSemana}
                    />
                    <Legend formatter={v => v === "completos" ? "✓ Completo" : v === "parciales" ? "⚠ Parcial" : "✗ Sin horas"} />
                    <Bar dataKey="completos" stackId="a" fill="#4CAF50" radius={[0,0,0,0]} />
                    <Bar dataKey="parciales" stackId="a" fill="#ED9519" />
                    <Bar dataKey="sinHoras"  stackId="a" fill="#EF5350" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              {/* Heatmap tabla */}
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Horas cargadas por usuario y día
                </Typography>

                {/* Leyenda */}
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  {[
                    { color: "#81C784", label: `≥ ${rangoData.horasJornada} hs — Completo` },
                    { color: "#FFB74D", label: "< 8 hs — Parcial" },
                    { color: "#E0E0E0", label: "0 hs — Sin horas" },
                  ].map(({ color, label }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: 0.5 }} />
                      <Typography variant="caption">{label}</Typography>
                    </Box>
                  ))}
                </Box>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 400 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700, position: "sticky", left: 0, zIndex: 2, minWidth: 160 }}>
                          Usuario
                        </TableCell>
                        {rangoData.dias.map(dia => (
                          <TableCell key={dia} align="center" sx={{ bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700, fontSize: "0.75rem", p: "6px 4px", minWidth: 70 }}>
                            {fmtDiaSemana(dia)}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ bgcolor: "#ED9519", color: "#1a0e00", fontWeight: 700, fontSize: "0.75rem" }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangoData.usuarios.map((u, i) => {
                        const totalU = Object.values(u.porDia).reduce((a, b) => a + b, 0);
                        return (
                          <TableRow key={u.id}>
                            <TableCell sx={{
                              fontWeight: 600, fontSize: "0.82rem",
                              position: "sticky", left: 0, zIndex: 1,
                              bgcolor: i % 2 === 0 ? "background.paper" : "action.hover",
                            }}>
                              {u.nombre}
                            </TableCell>
                            {rangoData.dias.map(dia => (
                              <HeatCell key={dia} hs={u.porDia[dia] || 0} jornada={rangoData.horasJornada} />
                            ))}
                            <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "#FFF3E0", color: "#c97d0e" }}>
                              {totalU.toFixed(1)} hs
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </LocalizationProvider>
      )}

      {/* ══════════════════════ MODAL EDICIÓN ═══════════════════════════════ */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700 }}>
          Editar horas
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important", pb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* ── Cabecera editable ── */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1.5} textTransform="uppercase" letterSpacing={0.5}>
                  Cabecera
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField label="OT *" size="small" fullWidth
                      value={editCabecera.ot}
                      onChange={e => setEditCabecera(p => ({ ...p, ot: e.target.value }))}
                      error={!editCabecera.ot?.trim() && !editCabecera.serie?.trim()}
                      helperText={!editCabecera.ot?.trim() && !editCabecera.serie?.trim() ? "Requerido uno de los dos" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Serie *" size="small" fullWidth
                      value={editCabecera.serie}
                      onChange={e => setEditCabecera(p => ({ ...p, serie: e.target.value }))}
                      error={!editCabecera.ot?.trim() && !editCabecera.serie?.trim()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DatePicker label="Fecha" value={editCabecera.fecha ? dayjs(editCabecera.fecha) : null}
                      onChange={v => setEditCabecera(p => ({ ...p, fecha: v ? v.toISOString() : null }))}
                      slotProps={{ textField: { size: "small", fullWidth: true } }} />
                  </Grid>
                </Grid>
              </Paper>
              {editLineas.map((linea, idx) => {
                const conflicto = getConflictoEdit(editLineas, idx);
                const incompleto = !linea.horaInicio || !linea.horaFin;
                const badge = incompleto ? "incompleto" : conflicto ? "solapado" : "ok";
                return (
                  <Paper key={idx} variant="outlined" sx={{
                    p: 2, borderRadius: 2, position: "relative",
                    borderColor: badge === "ok" ? "#4CAF50" : badge === "solapado" ? "#d32f2f" : "#bbb",
                    borderWidth: 2,
                  }}>
                    {/* Badge + delete */}
                    <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip size="small" label={
                        badge === "ok" ? "✓ OK"
                        : badge === "solapado" ? `⚠ Solapa línea ${conflicto}`
                        : "Incompleto"
                      } sx={{
                        fontWeight: 700, fontSize: "0.7rem",
                        bgcolor: badge === "ok" ? "#4CAF50" : badge === "solapado" ? "#d32f2f" : "#9E9E9E",
                        color: "#fff",
                      }} />
                      {editLineas.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => eliminarLineaEdit(idx, linea.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" fontWeight={700} mb={1.5} display="block">
                      Línea {idx + 1}
                    </Typography>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField label="Tipo" select size="small" fullWidth
                          value={linea.tipoTarea || ""}
                          onChange={e => actualizarLineaEdit(idx, "tipoTarea", e.target.value)}>
                          <MenuItem value="Mantenimiento">Mantenimiento</MenuItem>
                          <MenuItem value="Limpieza">Limpieza</MenuItem>
                          <MenuItem value="Otros">Otros</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TimePicker label="Hora inicio" ampm={false}
                          value={linea.horaInicio ? dayjs(linea.horaInicio) : null}
                          onChange={v => actualizarLineaEdit(idx, "horaInicio",
                            v ? combinarFechaHora(editCabecera.fecha, v) : null)}
                          slotProps={{ textField: { size: "small", fullWidth: true } }} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TimePicker label="Hora fin" ampm={false}
                          value={linea.horaFin ? dayjs(linea.horaFin) : null}
                          onChange={v => actualizarLineaEdit(idx, "horaFin",
                            v ? combinarFechaHora(editCabecera.fecha, v) : null)}
                          slotProps={{ textField: { size: "small", fullWidth: true } }} />
                      </Grid>
                      <Grid item xs={6} sm={3} md={1.5}>
                        <TextField label="Viaje ida" size="small" fullWidth type="number"
                          inputProps={{ min: 0, step: 0.5 }}
                          value={linea.tiempoViajeIda ?? ""}
                          onChange={e => actualizarLineaEdit(idx, "tiempoViajeIda", e.target.value)} />
                      </Grid>
                      <Grid item xs={6} sm={3} md={1.5}>
                        <TextField label="Viaje vuelta" size="small" fullWidth type="number"
                          inputProps={{ min: 0, step: 0.5 }}
                          value={linea.tiempoViajeVuelta ?? ""}
                          onChange={e => actualizarLineaEdit(idx, "tiempoViajeVuelta", e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label="Descripción" size="small" fullWidth multiline maxRows={3}
                          value={linea.descripcion || ""}
                          onChange={e => actualizarLineaEdit(idx, "descripcion", e.target.value)} />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}

              {/* Agregar línea */}
              <Button startIcon={<AddIcon />} onClick={agregarLineaEdit}
                sx={{
                  border: "2px dashed", borderColor: "divider", color: "text.secondary",
                  fontWeight: 600, py: 1.5, borderRadius: 2, justifyContent: "flex-start", pl: 2,
                  "&:hover": { borderColor: "#ED9519", color: "#ED9519", bgcolor: "transparent" },
                }}>
                Agregar línea
              </Button>
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEdicion}
            disabled={editSaving || editLineas.some((_, idx) => getConflictoEdit(editLineas, idx) !== null) || (!editCabecera.ot?.trim() && !editCabecera.serie?.trim())}
            sx={{ bgcolor: "#ED9519", "&:hover": { bgcolor: "#c97d0e" }, fontWeight: 700 }}>
            {editSaving ? <CircularProgress size={18} color="inherit" /> : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
